import jsbarcode from 'jsbarcode';
import type * as React from 'react';
import {useMemo} from 'react';
import type {IdCard} from '../../utils/IdCard';

type Props = {
    idCard: IdCard;
    withoutRoot?: boolean;
    transform?: string;
    height?: number;
    anchor?: 'start' | 'middle';
};

type BarcodeData = {
    encodings?: Array<{
        data: string;
    }>;
};

type BarcodeBlock = {
    x: number;
    width: number;
};

const Barcode: React.FC<Props> = ({
    idCard,
    withoutRoot,
    transform,
    anchor,
    height = 100,
}: Props) => {
    const [blocks, length] = useMemo(() => {
        const barcodeData: BarcodeData = {};
        jsbarcode(barcodeData, idCard.id, {format: 'code39'});

        if (!barcodeData.encodings || barcodeData.encodings.length === 0) {
            throw new Error('Failed to create barcode');
        }

        const blocks: BarcodeBlock[] = [];
        const data = barcodeData.encodings[0].data;

        for (let i = 0; i < data.length; ++i) {
            if (data[i] === '0') {
                continue;
            }

            const x = i;

            for (let j = i + 1; j < data.length; ++j) {
                if (data[j] === '0') {
                    break;
                }

                ++i;
            }

            blocks.push({x, width: i - x + 1});
        }

        return [blocks, data.length];
    }, [idCard]);

    const barcode = (
        <g transform={anchor === 'middle' ? `translate(-${(length * 5) / 2}, 0)` : undefined}>
            <g transform={transform}>
                {blocks.map(block => (
                    <rect
                        key={`${block.x}-${block.width}`}
                        x={block.x * 5}
                        y={0}
                        width={block.width * 5}
                        height={height}
                    />
                ))}

                <text x={(length * 5) / 2} y={height + 50} textAnchor='middle' fontSize={50}>
                    {idCard.id}
                </text>
            </g>
        </g>
    );

    if (withoutRoot) {
        return barcode;
    }

    return (
        <svg viewBox={`0 0 ${length * 5} 160`}>
            <title id={`barcode-title-${idCard.id}`}>Barcode for ID {idCard.id}</title>
            {barcode}
        </svg>
    );
};

export default Barcode;
